import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import AddDepartmentDialog from './AddDepartmentDialog';
import EditDepartmentDialog from './EditDepartmentDialog';
import AddFacultyStaffDialog from './AddFacultyStaffDialog';
import EditFacultyStaffDialog from './EditFacultyStaffDialog';
import './FacultyDetails.css';

const FacultyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [faculty, setFaculty] = useState(location.state?.faculty || null);
  const [departments, setDepartments] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [draggedDepartment, setDraggedDepartment] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [dragOverDepartment, setDragOverDepartment] = useState(null);
  const [activeTab, setActiveTab] = useState('departments');
  const [isAddStaffDialogOpen, setIsAddStaffDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [isEditStaffDialogOpen, setIsEditStaffDialogOpen] = useState(false);
  const [draggedStaff, setDraggedStaff] = useState(null);
  const [isReorderingStaff, setIsReorderingStaff] = useState(false);
  const [dragOverStaff, setDragOverStaff] = useState(null);

  const fetchDepartments = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch departments for this faculty
      const departmentsResponse = await api.get(`/departments?faculty_ref=${id}`);
      if (departmentsResponse.data.success) {
        setDepartments(departmentsResponse.data.data);
      } else {
        setDepartments([]);
      }

      // Fetch staffs for this faculty
      const staffsResponse = await api.get(`/faculty-staffs?faculty_ref=${id}`);
      if (staffsResponse.data.success) {
        setStaffs(staffsResponse.data.data);
      } else {
        setStaffs([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else {
        setError('Error loading data. Please try again.');
      }
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (faculty) {
      fetchDepartments();
    }
  }, [faculty, fetchDepartments]);

  useEffect(() => {
    const fetchFaculty = async () => {
      if (faculty) {
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await api.get('/faculties');

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to fetch faculty');
        }

        const matchedFaculty = response.data.data.find(item => item._id === id);
        if (!matchedFaculty) {
          throw new Error('Faculty not found');
        }

        setFaculty(matchedFaculty);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Faculty not found');
        setLoading(false);
      }
    };

    fetchFaculty();
  }, [faculty, id]);

  // Drag and Drop handlers
  const handleDragStart = (e, department) => {
    setDraggedDepartment(department);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, department) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedDepartment && draggedDepartment._id !== department._id) {
      setDragOverDepartment(department._id);
    }
  };

  const handleDrop = (e, targetDepartment) => {
    e.preventDefault();
    
    if (!draggedDepartment || draggedDepartment._id === targetDepartment._id) {
      return;
    }

    const draggedIndex = departments.findIndex(d => d._id === draggedDepartment._id);
    const targetIndex = departments.findIndex(d => d._id === targetDepartment._id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Create new array with reordered departments
    const newDepartments = [...departments];
    const [removed] = newDepartments.splice(draggedIndex, 1);
    newDepartments.splice(targetIndex, 0, removed);

    setDepartments(newDepartments);
    setDraggedDepartment(null);
    setDragOverDepartment(null);
    setIsReordering(true);
  };

  const handleDragEnd = () => {
    setDraggedDepartment(null);
    setDragOverDepartment(null);
  };

  // Send updated order to backend
  const sendUpdatedOrder = React.useCallback(async () => {
    if (!isReordering) {
      return;
    }

    try {
      const departmentIds = departments.map(dept => dept._id);

      const response = await api.post('/dept-priorities', {
        faculty_ref: id,
        department_ids: departmentIds
      });

      if (response.data.success) {
        setIsReordering(false);
      } else {
        // Revert to original order if update fails
        fetchDepartments();
      }
    } catch (err) {
      // Revert to original order if update fails
      fetchDepartments();
    } finally {
      setIsReordering(false);
    }
  }, [departments, isReordering, id, fetchDepartments]);

  // Auto-save order after reordering
  useEffect(() => {
    if (isReordering) {
      const timeoutId = setTimeout(() => {
        sendUpdatedOrder();
      }, 1000); // Wait 1 second after last reorder

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isReordering, sendUpdatedOrder]);



  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setIsAddDialogOpen(false);
  };

  const handleAddSave = (newDepartment) => {
    setDepartments(prevDepartments => [...prevDepartments, newDepartment]);
  };

  const handleEditClick = (department) => {
    setEditingDepartment(department);
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    setEditingDepartment(null);
  };

  const handleEditSave = (updatedDepartment) => {
    setDepartments(prevDepartments => 
      prevDepartments.map(dept => 
        dept._id === updatedDepartment._id ? updatedDepartment : dept
      )
    );
  };

  const handleDeleteDepartment = async (departmentId) => {
    if (!window.confirm('Are you sure you want to delete this department? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/departments/${departmentId}`);
      
      if (response.data.success) {
        setDepartments(prevDepartments => 
          prevDepartments.filter(dept => dept._id !== departmentId)
        );
      } else {
        alert('Failed to delete department');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        alert('Unauthorized. Please log in again.');
      } else {
        alert('Error deleting department. Please try again.');
      }
    }
  };

  const handleViewTeachers = (department) => {
    navigate(`/faculties/${faculty._id}/departments/${department._id}`, {
      state: { 
        faculty: faculty,
        department: department 
      }
    });
  };

  const handleAddStaffClick = () => {
    setIsAddStaffDialogOpen(true);
  };

  const handleAddStaffClose = () => {
    setIsAddStaffDialogOpen(false);
  };

  const handleAddStaffSave = (newStaff) => {
    setStaffs(prevStaffs => [...prevStaffs, newStaff]);
  };

  const handleEditStaffClick = (staff) => {
    setEditingStaff(staff);
    setIsEditStaffDialogOpen(true);
  };

  const handleEditStaffClose = () => {
    setIsEditStaffDialogOpen(false);
    setEditingStaff(null);
  };

  const handleEditStaffSave = (updatedStaff) => {
    setStaffs(prevStaffs => 
      prevStaffs.map(staff => 
        staff._id === updatedStaff._id ? updatedStaff : staff
      )
    );
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/faculty-staffs/${staffId}`);
      
      if (response.data.success) {
        setStaffs(prevStaffs => 
          prevStaffs.filter(staff => staff._id !== staffId)
        );
      } else {
        alert('Failed to delete staff member');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        alert('Unauthorized. Please log in again.');
      } else {
        alert('Error deleting staff member. Please try again.');
      }
    }
  };

  // Staff Drag and Drop handlers
  const handleStaffDragStart = (e, staff) => {
    setDraggedStaff(staff);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleStaffDragOver = (e, staff) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedStaff && draggedStaff._id !== staff._id) {
      setDragOverStaff(staff._id);
    }
  };

  const handleStaffDrop = (e, targetStaff) => {
    e.preventDefault();
    
    if (!draggedStaff || draggedStaff._id === targetStaff._id) {
      return;
    }

    const draggedIndex = staffs.findIndex(s => s._id === draggedStaff._id);
    const targetIndex = staffs.findIndex(s => s._id === targetStaff._id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Create new array with reordered staffs
    const newStaffs = [...staffs];
    const [removed] = newStaffs.splice(draggedIndex, 1);
    newStaffs.splice(targetIndex, 0, removed);

    // Update priorities based on new order
    const updatedStaffs = newStaffs.map((staff, index) => ({
      ...staff,
      priority: index + 1
    }));

    setStaffs(updatedStaffs);
    setDraggedStaff(null);
    setDragOverStaff(null);
    setIsReorderingStaff(true);
  };

  const handleStaffDragEnd = () => {
    setDraggedStaff(null);
    setDragOverStaff(null);
  };

  // Send updated staff priorities to backend
  const sendUpdatedStaffPriorities = React.useCallback(async () => {
    if (!isReorderingStaff) {
      return;
    }

    try {
      // Update each staff's priority individually
      const updatePromises = staffs.map(staff => 
        api.put(`/faculty-staffs/${staff._id}`, {
          name: staff.name,
          email: staff.email,
          designation: staff.designation,
          faculty_ref: id,
          priority: staff.priority,
          photoUrl: staff.photoUrl || staff.dp || ''
        })
      );

      await Promise.all(updatePromises);
      setIsReorderingStaff(false);
    } catch (err) {
      console.error('Error updating staff priorities:', err);
      // Revert to original order if update fails
      fetchDepartments();
      setIsReorderingStaff(false);
    }
  }, [staffs, isReorderingStaff, id, fetchDepartments]);

  // Auto-save staff priorities after reordering
  useEffect(() => {
    if (isReorderingStaff) {
      const timeoutId = setTimeout(() => {
        sendUpdatedStaffPriorities();
      }, 1000); // Wait 1 second after last reorder

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isReorderingStaff, sendUpdatedStaffPriorities]);

  if (loading) {
    return (
      <div className="faculty-details-loading">
        <div className="loading-spinner"></div>
        <p>Loading faculty details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="faculty-details-error">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!faculty) {
    return (
      <div className="faculty-details-error">
        <div className="error-content">
          <h2>Faculty Not Found</h2>
          <p>The faculty you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="faculty-details">
      <header className="details-header">
        <div className="faculty-title">
          <h1>{faculty.name}</h1>
          <p className="faculty-subtitle">{faculty.nameEn}</p>
        </div>
      </header>

      <main className="details-main">
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'departments' ? 'active' : ''}`}
            onClick={() => setActiveTab('departments')}
          >
            <span className="tab-icon">🏢</span>
            <span className="tab-text">
              <span className="tab-label">Departments</span>
              <span className="tab-count">{departments.length}</span>
            </span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'staffs' ? 'active' : ''}`}
            onClick={() => setActiveTab('staffs')}
          >
            <span className="tab-icon">👥</span>
            <span className="tab-text">
              <span className="tab-label">Dean Office</span>
              <span className="tab-count">{staffs.length}</span>
            </span>
          </button>
        </div>

        {/* Departments Section */}
        {activeTab === 'departments' && (
          <div className="departments-section">
          <div className="section-header">
            <div className="header-content">
              <div className="header-text">
                <h2>Departments</h2>
                <p>{departments.length} department{departments.length !== 1 ? 's' : ''} found</p>
                {departments.length > 1 && (
                  <p className="reorder-hint">💡 Drag and drop to reorder departments</p>
                )}
              </div>
              <button className="add-department-button" onClick={handleAddClick}>
                <span className="button-icon">+</span>
                Add Department
              </button>
            </div>
          </div>

          {departments.length > 0 ? (
            <div className="departments-list">
              {departments.map((department) => (
                <div
                  key={department._id}
                  className={`department-item ${draggedDepartment?._id === department._id ? 'dragging' : ''} ${dragOverDepartment === department._id ? 'drag-over' : ''}`}
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, department)}
                  onDragOver={(e) => handleDragOver(e, department)}
                  onDrop={(e) => handleDrop(e, department)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="department-header">
                    <div className="drag-icon-container">
                      <span className="drag-icon">⋮⋮</span>
                    </div>
                    <div className="department-info">
                      <h3 className="department-name">{department.name}</h3>
                      <p className="department-name-en">{department.nameEn}</p>
                    </div>
                  </div>
                  <div className="department-actions">
                    <button 
                      className="view-department-btn"
                      onClick={() => handleViewTeachers(department)}
                    >
                      View Teachers
                    </button>
                    <button 
                      className="edit-department-btn"
                      onClick={() => handleEditClick(department)}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-department-btn"
                      onClick={() => handleDeleteDepartment(department._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {isReordering && (
                <div className="reorder-status">
                  <span className="status-indicator">⏳</span>
                  Updating department order...
                </div>
              )}
            </div>
          ) : (
            <div className="no-departments">
              <div className="empty-state">
                <div className="empty-icon">🏢</div>
                <h3>No Departments Yet</h3>
                <p>This faculty doesn't have any departments yet.</p>
                <button className="add-department-btn" onClick={handleAddClick}>
                  Add First Department
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Staffs Section */}
        {activeTab === 'staffs' && (
          <div className="staffs-section">
            <div className="section-header">
              <div className="header-content">
                <div className="header-text">
                  <h2>Dean Office</h2>
                  <p>{staffs.length} staff member{staffs.length !== 1 ? '' : ''} found</p>
                </div>
                <button className="add-staff-button" onClick={handleAddStaffClick}>
                  <span className="button-icon">+</span>
                  Add Staff
                </button>
              </div>
            </div>

            {staffs.length > 0 ? (
              <div className="staffs-grid">
                {staffs.map((staff) => (
                  <div 
                    key={staff._id} 
                    className={`staff-card ${draggedStaff?._id === staff._id ? 'dragging' : ''} ${dragOverStaff === staff._id ? 'drag-over' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleStaffDragStart(e, staff)}
                    onDragOver={(e) => handleStaffDragOver(e, staff)}
                    onDrop={(e) => handleStaffDrop(e, staff)}
                    onDragEnd={handleStaffDragEnd}
                  >
                    <div className="staff-card-header">
                      <div className="staff-identity">
                        <div className="staff-avatar">
                          {(staff.photoUrl || staff.dp) ? (
                            <img src={staff.photoUrl || staff.dp} alt={staff.name} className="avatar-image" loading="lazy" decoding="async" />
                          ) : (
                            <span className="avatar-text">{staff.name?.charAt(0) || 'S'}</span>
                          )}
                        </div>
                        <div className="staff-title-block">
                          <h3 className="staff-name">{staff.name}</h3>
                          <p className="staff-designation">{staff.designation}</p>
                        </div>
                      </div>
                      <div className="drag-icon-container">
                        <span className="drag-icon">⋮⋮</span>
                      </div>
                    </div>
                    <div className="staff-card-body">
                      {staff.phoneNumber && (
                        <p className="staff-phone">📞 {staff.phoneNumber}</p>
                      )}
                      {staff.email && (
                        <p className="staff-email">✉️ {staff.email}</p>
                      )}
                    </div>
                    <div className="staff-card-actions">
                      <button 
                        className="edit-staff-btn"
                        onClick={() => handleEditStaffClick(staff)}
                      >
                        Edit
                      </button>
                      <button 
                        className="delete-staff-btn"
                        onClick={() => handleDeleteStaff(staff._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {isReorderingStaff && (
                  <div className="reorder-status-staff">
                    <span className="status-indicator">⏳</span>
                    Updating staff order...
                  </div>
                )}
              </div>
            ) : (
              <div className="no-staffs">
                <div className="empty-state">
                  <div className="empty-icon">👥</div>
                  <h3>No Faculty Staffs Yet</h3>
                  <p>This faculty doesn't have any staff members yet.</p>
                  <button className="add-staff-btn" onClick={handleAddStaffClick}>
                    Add First Staff
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <AddDepartmentDialog
        faculty={faculty}
        isOpen={isAddDialogOpen}
        onClose={handleAddClose}
        onSave={handleAddSave}
      />

      <EditDepartmentDialog
        department={editingDepartment}
        faculty={faculty}
        isOpen={isEditDialogOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />

      <AddFacultyStaffDialog
        isOpen={isAddStaffDialogOpen}
        onClose={handleAddStaffClose}
        onSave={handleAddStaffSave}
        faculty={faculty}
      />

      <EditFacultyStaffDialog
        isOpen={isEditStaffDialogOpen}
        onClose={handleEditStaffClose}
        onSave={handleEditStaffSave}
        staff={editingStaff}
        faculty={faculty}
      />
    </div>
  );
};

export default FacultyDetails;
